<template>
  <b-sidebar
    :id="'add-new-task-sidebar' && expanded ? 'mein-expanded-view' : ' '"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg task-detail-sidebar"
    shadow
    backdrop
    :no-close-on-backdrop="
      $refs.refFormObserver && $refs.refFormObserver.flags.dirty
    "
    no-header
    right
    @hidden="resetForm"
    @change="
      (val) => {
        if (!val) $emit('reset-is-editing-to-false')
        $emit('update:is-sidebar-active', val)
      }
    "
  >
    <template #default="{ hide }">
      <div class="sidebar-wrapper d-flex flex-column h-100">
        <!-- HEADER -->
        <div class="sidebar-header-section content-sidebar-header px-2 py-1 bg-white border-bottom flex-shrink-0">
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1 mr-1">
              <!-- Task Title -->
              <h4 class="mb-25 font-weight-bolder text-dark">
                {{ isEditing ? (form.name || value.name) : 'Add New Task' }}
              </h4>

              <!-- Subtitle: ProcessHandle • Category Link -->
              <div
                v-if="parentProcess"
                class="d-flex align-items-center flex-wrap font-small-3 text-muted mb-25"
              >
                <span class="font-weight-bold text-dark mr-50">
                  {{ parentProcess.processHandle }}
                </span>
                <span v-if="categoryName" class="mr-50">•</span>
                <a
                  v-if="categoryName"
                  href="#"
                  class="text-primary font-weight-bold d-inline-flex align-items-center mr-50"
                  @click.prevent="gotoProcess(parentProcess)"
                >
                  {{ categoryName }}
                  <feather-icon icon="ExternalLinkIcon" size="13" class="ml-25" />
                </a>
              </div>

              <!-- Updated line -->
              <div v-if="updatedInfoText" class="font-small-2 text-muted">
                {{ updatedInfoText }}
              </div>
            </div>

            <!-- Header Right Icons & Status Chip -->
            <div class="d-flex flex-column align-items-end">
              <div class="d-flex align-items-center mb-50">
                <feather-icon
                  :target="`expand-minimize-view`"
                  class="cursor-pointer text-muted mr-1"
                  :icon="expanded ? 'MinimizeIcon' : 'MaximizeIcon'"
                  size="16"
                  @click="sideResize"
                />
                <feather-icon
                  class="cursor-pointer text-muted"
                  icon="XIcon"
                  size="18"
                  @click="hide"
                />
              </div>

              <!-- Status Chip -->
              <b-badge
                v-if="statusChip"
                pill
                :variant="statusChip.variant"
                class="px-1 py-50 font-small-2 font-weight-bold"
              >
                {{ statusChip.label }}
              </b-badge>
            </div>
          </div>
        </div>

        <!-- SCROLLABLE BODY -->
        <div class="sidebar-body-section flex-grow-1 overflow-auto">
          <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
            <b-form
              id="task-detail-form"
              class="p-2 task-detail-form"
              @submit.prevent="handleSubmit(onSubmit)"
              @reset.prevent="resetForm"
            >
              <!-- When Creating a New Task: Template & Name Pickers -->
              <div v-if="!isEditing" class="mb-2">
                <b-row>
                  <!-- Task Template -->
                  <b-col cols="12" class="mb-1">
                    <validation-provider
                      #default="validationContext"
                      name="Task Template"
                      rules="required"
                    >
                      <b-form-group
                        label="Task Template"
                        label-for="process"
                        :state="getValidationState(validationContext)"
                      >
                        <v-select
                          v-model="form.processId"
                          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                          :options="processOptions"
                          :reduce="(val) => val.value"
                          :clearable="false"
                          class="wrapped-li"
                          input-id="process"
                          @input="onTemplateSelect"
                        />
                        <b-form-invalid-feedback
                          :state="getValidationState(validationContext)"
                        >
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <!-- Task Name -->
                  <b-col cols="12">
                    <validation-provider
                      #default="validationContext"
                      name="Task Name"
                      rules="required"
                    >
                      <b-form-group
                        label="Task Name"
                        label-for="task-name"
                        :state="getValidationState(validationContext)"
                      >
                        <b-form-input
                          id="task-name"
                          v-model="form.name"
                          maxlength="255"
                          :state="getValidationState(validationContext)"
                        />
                        <b-form-invalid-feedback>
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>
              </div>

              <!-- EDITABLE TASK FIELDS -->
              <b-row>
                <!-- Status -->
                <b-col cols="12" md="6" class="mb-1">
                  <b-form-group label="Status" label-for="status">
                    <v-select
                      v-model="form.status"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="statusOptions"
                      :reduce="(val) => val.value"
                      :clearable="false"
                      class="wrapped-li"
                      input-id="status"
                    />
                  </b-form-group>
                </b-col>

                <!-- Due Date -->
                <b-col cols="12" md="6" class="mb-1">
                  <b-form-group label="Due date" label-for="dueDate">
                    <b-form-datepicker
                      id="dueDate"
                      v-model="form.dueDate"
                      :date-format-options="{
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        weekday: 'short',
                      }"
                      locale="en"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Assignee -->
              <b-row class="mb-1">
                <b-col cols="12">
                  <b-form-group label="Assignee" label-for="assignee">
                    <v-select
                      v-model="form.userId"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="userOptions"
                      :reduce="(val) => val.value"
                      :clearable="false"
                      :disabled="currentRouteName === 'my-task-list'"
                      class="wrapped-li"
                      input-id="assignee"
                      @input="OnOwnerChange"
                    />

                    <!-- Supplemental Assignee / Reassigned Text -->
                    <div v-if="isReassigned" class="text-muted font-small-3 mt-50">
                      Reassigned from <span class="font-weight-bold text-dark">{{ originalAssigneeName }}</span> for this occurrence
                    </div>
                    <div v-else-if="roleSupplemental" class="text-muted font-small-3 mt-50">
                      as
                      <span
                        v-if="roleSupplemental.role"
                        class="hyperlink-underlined cursor-pointer text-primary font-weight-bold"
                        @click="gotoRole(roleSupplemental.role)"
                      >
                        {{ roleSupplemental.text }}
                        <feather-icon icon="ExternalLinkIcon" size="12" class="ml-25" />
                      </span>
                      <span v-else class="font-weight-bold text-dark">
                        {{ roleSupplemental.text }}
                      </span>
                    </div>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Notes for this occurrence -->
              <b-row class="mb-2">
                <b-col cols="12">
                  <label class="label-edit font-weight-bold mb-50">
                    Notes for this occurrence
                  </label>
                  <QuillEditor
                    id="task-occurrence-notes"
                    mode="notes"
                    :description.sync="form.description"
                    :is-readonly.sync="disabled"
                    @assign="changeDesc"
                  />
                </b-col>
              </b-row>

              <!-- EMBEDDED PARENT TEMPLATE CARD -->
              <div
                v-if="parentProcess"
                class="template-embed-card p-2 mb-2 rounded"
              >
                <!-- Template Header -->
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="font-weight-bolder text-dark font-medium-1">
                    Template {{ parentProcess.processHandle }}
                  </div>
                  <a
                    href="#"
                    class="font-small-3 font-weight-bold text-primary d-inline-flex align-items-center"
                    @click.prevent="gotoProcess(parentProcess)"
                  >
                    Edit template
                    <feather-icon icon="ExternalLinkIcon" size="13" class="ml-25" />
                  </a>
                </div>

                <!-- Description -->
                <div class="mb-1">
                  <div class="text-muted font-small-2 font-weight-bold text-uppercase mb-25">
                    Description
                  </div>
                  <div
                    v-if="parentProcess.description"
                    class="font-small-3 text-body ql-editor-preview"
                    v-html="parentProcess.description"
                  />
                  <div v-else class="font-small-3 text-muted">
                    No description provided.
                  </div>
                </div>

                <!-- Resources -->
                <div class="mb-1">
                  <div class="text-muted font-small-2 font-weight-bold text-uppercase mb-25">
                    Resources
                  </div>
                  <div v-if="parentProcess.docs && parentProcess.docs.length > 0" class="d-flex flex-wrap">
                    <a
                      v-for="doc in parentProcess.docs"
                      :key="doc.id || doc.value"
                      :href="doc.value"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="badge badge-light-primary mr-50 mb-50 px-75 py-50 d-inline-flex align-items-center"
                    >
                      <span>{{ doc.name || doc.value }}</span>
                      <feather-icon icon="ExternalLinkIcon" size="12" class="ml-25" />
                    </a>
                  </div>
                  <div v-else class="font-small-3 text-muted">
                    No resources attached.
                  </div>
                </div>

                <!-- Frequency & Template Owner Grid -->
                <b-row class="mb-1">
                  <!-- Frequency -->
                  <b-col cols="12" sm="6" class="mb-1 mb-sm-0">
                    <div class="text-muted font-small-2 font-weight-bold text-uppercase mb-25">
                      Frequency
                    </div>
                    <div class="d-flex align-items-center">
                      <ProcessFrequencyTiles
                        v-if="
                          parentProcess.frequencyType === 'Scheduled' &&
                          parentProcess.frequency &&
                          frequencyKeys(Object.keys(parentProcess.frequency), parentProcess.frequency)
                        "
                        size="sm"
                        :overlay-text="getOverlayText(parentProcess.frequency)"
                        :recurring-months="
                          getRecurringMonths(parentProcess.frequency, currentYear).months
                        "
                      />
                      <span v-else-if="parentProcess.frequencyType === 'AsNeeded'" class="font-small-3 text-body font-weight-bold">
                        As Needed
                      </span>
                      <span v-else class="font-small-3 text-muted">
                        Dates not set
                      </span>
                    </div>
                  </b-col>

                  <!-- Template Owner / Also Notifies -->
                  <b-col cols="12" sm="6">
                    <div class="text-muted font-small-2 font-weight-bold text-uppercase mb-25">
                      Template owner
                    </div>
                    <div class="font-small-3 font-weight-bold text-dark">
                      {{ templateOwnerText }}
                    </div>
                  </b-col>
                </b-row>

                <!-- Labels -->
                <div v-if="parentProcess.labels && parentProcess.labels.length > 0">
                  <div class="text-muted font-small-2 font-weight-bold text-uppercase mb-25">
                    Labels
                  </div>
                  <div class="d-flex flex-wrap">
                    <label-chip :labels="parentProcess.labels" />
                  </div>
                </div>
              </div>
            </b-form>
          </validation-observer>
        </div>

        <!-- FOOTER ACTIONS -->
        <div class="sidebar-footer-section d-flex justify-content-between align-items-center px-2 py-1 bg-white border-top flex-shrink-0">
          <!-- Left: Mark Done Button -->
          <div>
            <b-button
              v-ripple.400="'rgba(40, 199, 111, 0.15)'"
              type="button"
              variant="outline-success"
              class="d-flex align-items-center"
              :disabled="isLoading"
              @click="markAsDone"
            >
              <feather-icon icon="CheckIcon" size="14" class="mr-50" />
              <span>{{ form.status === 'Complete' ? 'Completed' : 'Mark done' }}</span>
            </b-button>
          </div>

          <!-- Right: Cancel & Save Buttons -->
          <div class="d-flex align-items-center">
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              class="mr-1"
              @click="hide"
            >
              Cancel
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              type="button"
              :disabled="isLoading"
              @click="$refs.refFormObserver ? $refs.refFormObserver.handleSubmit(onSubmit)() : onSubmit()"
            >
              Save
            </b-button>
          </div>
        </div>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import moment from "moment-timezone"
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormDatepicker,
  BBadge,
  BCol,
  BRow,
  VBTooltip,
} from "bootstrap-vue"
import { mapActions, mapState } from "vuex"
import Ripple from "vue-ripple-directive"
import vSelect from "vue-select"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import { required, alphaNum, email } from "@validations"
import Enums from "@/utils/enums"
import { formatDate } from "@/utils/date-utils"
import ability from "@/libs/acl/ability"
import QuillEditor from "@/views/shared/QuillEditor.vue"
import ProcessFrequencyTiles from "@/views/process/ProcessFrequencyTiles.vue"
import LabelChip from "@/views/shared/LabelChip.vue"
import { getRecurringMonths } from "pbshared"

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    BFormDatepicker,
    BBadge,
    BCol,
    BRow,
    vSelect,
    QuillEditor,
    ProcessFrequencyTiles,
    LabelChip,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    init: Boolean,
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    isReadonly: {
      type: Boolean,
    },
    value: {
      type: Object,
      default: () => null,
    },
    editTab: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      form: this.getDefaultForm(),
      statusOptions: Enums.taskStatusOptions,
      disabled: false,
      expanded: false,
      permanet: false,
    }
  },
  computed: {
    ...mapState("app", ["isLoading"]),
    ...mapState("task", ["task"]),
    ...mapState("process", ["processes", "allProcesses"]),
    ...mapState("user", ["users", "currentUser", "allUsers"]),
    isEditing() {
      return !!this.value
    },
    currentYear() {
      return new Date().getFullYear()
    },
    userOptions() {
      return this.allUsers.nodes.map((item) => ({
        label: item.name,
        value: item.id,
        ...item,
      }))
    },
    processOptions() {
      return this.allProcesses.nodes.map((item) => ({
        label: `${item.processHandle}: ${item.name}`,
        value: item.id,
        ...item,
      }))
    },
    currentRouteName() {
      return this.$route.name
    },
    parentProcess() {
      const procId = this.form.processId || this.value?.processId
      const procHandle =
        this.form.processHandle ||
        this.value?.processHandle ||
        this.value?.process?.processHandle

      if (!procId && !procHandle) return null

      return (
        this.allProcesses.nodes.find(
          (p) =>
            (procId && p.id === procId) ||
            (procHandle && p.processHandle === procHandle)
        ) ||
        this.value?.process ||
        null
      )
    },
    categoryName() {
      if (!this.parentProcess?.cat) return ""
      return (
        this.parentProcess.cat.description ||
        this.parentProcess.cat.prefix ||
        this.parentProcess.cat.name ||
        ""
      )
    },
    statusChip() {
      if (!this.isEditing || !this.form.dueDate) return null
      const tz = this.$tenantTimezone || "America/Chicago"
      const dueDate = moment(this.form.dueDate).tz(tz).startOf("day")
      const today = moment().tz(tz).startOf("day")

      if (this.form.status === "Complete") {
        const doneDate = this.form.updated_at
          ? moment(this.form.updated_at).tz(tz).format("MMM D")
          : dueDate.format("MMM D")
        return {
          label: `Done ${doneDate}`,
          variant: "light-success",
        }
      }

      if (dueDate.isBefore(today)) {
        const daysLate = Math.max(1, today.diff(dueDate, "days"))
        return {
          label: `${daysLate} ${daysLate === 1 ? "day" : "days"} late`,
          variant: "light-danger",
        }
      }

      if (dueDate.isSame(today)) {
        return {
          label: "Due today",
          variant: "light-warning",
        }
      }

      return null
    },
    updatedInfoText() {
      const tz = this.$tenantTimezone || "America/Chicago"
      const updatedAt = this.form.updated_at || this.value?.updated_at
      if (updatedAt) {
        const dt = moment(updatedAt).tz(tz).format("MMM D")
        const user =
          this.value?.assignee?.name ||
          this.form.assignee?.name ||
          (this.currentUser ? this.currentUser.name : "")
        return user ? `Updated ${dt} by ${user}` : `Updated ${dt}`
      }
      const createdAt = this.form.created_at || this.value?.created_at
      if (createdAt) {
        const dt = moment(createdAt).tz(tz).format("MMM D")
        return `Created ${dt}`
      }
      return ""
    },
    originalAssigneeId() {
      const initHandle = this.form.init_handle || this.value?.init_handle
      if (initHandle) {
        const parts = initHandle.split("-")
        if (parts.length >= 2 && !isNaN(Number(parts[1]))) {
          return parts[1]
        }
      }
      return null
    },
    originalAssigneeName() {
      const origId = this.originalAssigneeId
      if (!origId) return "original assignee"
      const user = this.allUsers.nodes.find(
        (u) => String(u.id) === String(origId)
      )
      return user ? user.name : "original assignee"
    },
    isReassigned() {
      if (!this.isEditing) return false
      const currentUserId = this.form.userId
      const origId = this.originalAssigneeId
      if (currentUserId && origId && String(currentUserId) !== String(origId)) {
        return true
      }
      return false
    },
    roleSupplemental() {
      if (this.isReassigned) return null
      if (this.parentProcess?.assignedRoles?.length) {
        const role = this.parentProcess.assignedRoles[0]
        return {
          text: role.roleHandle ? `${role.roleHandle}: ${role.name}` : role.name,
          role,
        }
      }
      if (this.parentProcess?.role) {
        const role = this.parentProcess.role
        return {
          text: role.roleHandle ? `${role.roleHandle}: ${role.name}` : role.name,
          role,
        }
      }
      if (this.form.role || this.value?.role) {
        return {
          text: this.form.role || this.value?.role,
          role: null,
        }
      }
      return null
    },
    templateOwnerText() {
      if (this.parentProcess?.role) {
        const role = this.parentProcess.role
        return role.roleHandle ? `${role.roleHandle}: ${role.name}` : role.name
      }
      return "Not Assigned"
    },
  },
  watch: {
    value: {
      handler(val) {
        if (val) {
          const tz = this.$tenantTimezone || "America/Chicago"
          const dueDateFormatted = val.dueDate
            ? moment(val.dueDate).tz(tz).format("YYYY-MM-DD")
            : new Date()

          this.form = {
            ...val,
            dueDate: dueDateFormatted,
            __typename: undefined,
          }
        } else {
          this.form = this.getDefaultForm()
        }
      },
      immediate: true,
    },
    isSidebarActive(newValue) {
      if (newValue === true && !this.isEditing) {
        this.form = this.getDefaultForm()
      }
    },
  },
  mounted() {
    if (!this.$props.init) {
      const unwatch = this.$watch("init", () => {
        this.fetchData()
        unwatch()
      })
      return
    }
    this.fetchData()
  },
  methods: {
    getRecurringMonths,
    fetchData() {
      this.refetchProcessesIfNotLoaded()
      if (ability.can("add", "task")) {
        this.fetchUsersIfNotLoaded()
      }
    },
    ...mapActions("user", ["fetchUsersIfNotLoaded"]),
    ...mapActions("process", ["refetchProcessesIfNotLoaded"]),
    getOverlayText(frequency) {
      if (!frequency?.Interval?.RepeatEvery) return
      const r = frequency?.Interval?.RepeatEvery
      return `Every ${r === 1 ? "week" : r + " weeks"}`
    },
    frequencyKeys(keys, frequency) {
      if (!keys.length) return false
      return keys.some(
        (key) =>
          frequency[key] &&
          (frequency[key].days?.length ||
            (typeof frequency[key] === "object" &&
              Object.keys(frequency[key]).length > 0))
      )
    },
    generateHandle(processId, userId, dueDate, counterPart = "") {
      const formattedDate = formatDate(new Date(dueDate))
      return `${processId}-${userId}-${formattedDate}${counterPart}`
    },
    handleAction(action) {
      action.then(({ data }) => {
        const task = data.createTask || data.updateTask || data.updateFutureTask
        this.$emit("update:is-sidebar-active", false)
        this.$emit("regenerate-tasks-to-display", task)
        this.$emit("reset-is-editing-to-false")
      })
    },
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null
    },
    sideResize() {
      this.expanded = !this.expanded
    },
    changeDesc(value) {
      this.form.description = value
    },
    resetForm() {
      this.$emit("closed")
      this.permanet = false
      this.$emit("update:value", null)
      this.$nextTick(() => {
        if (this.$refs.refFormObserver) {
          this.$refs.refFormObserver.reset()
        }
      })
      this.form = this.getDefaultForm()
    },
    onTemplateSelect(processId) {
      const proc = this.allProcesses.nodes.find((p) => p.id === processId)
      if (proc) {
        this.form.name = proc.name
        if (proc.description) {
          this.form.description = proc.description
        }
      }
    },
    markAsDone() {
      this.form.status = "Complete"
      this.onSubmit()
    },
    onSubmit() {
      const { init_handle, userId, processId, dueDate } = this.form
      const origId = this.originalAssigneeId
      let initHandle =
        init_handle ||
        (origId
          ? this.generateHandle(processId, origId, dueDate)
          : this.generateHandle(processId, userId, dueDate))

      const dueDateInUtc = moment
        .tz(this.form.dueDate, "YYYY-MM-DD", this.$tenantTimezone)
        .toISOString()

      const taskHandle = this.generateHandle(processId, userId, dueDate)

      const payload = {
        name: this.form.name,
        init_handle: initHandle,
        handle: taskHandle,
        status: this.form.status,
        dueDate: dueDateInUtc,
      }

      if (processId !== null && processId !== undefined) {
        payload.processId = Number(processId)
      }
      if (userId !== null && userId !== undefined) {
        payload.userId = Number(userId)
      }
      if (this.form.description !== undefined) {
        payload.description = this.form.description
      }
      if (typeof this.form.deleted === "boolean") {
        payload.deleted = this.form.deleted
      }
      if (typeof this.form.is_late === "boolean") {
        payload.is_late = this.form.is_late
      }

      let action
      if (this.isEditing) {
        if (this.form.id) {
          payload.id = Number(this.form.id)
          action = this.$store.dispatch("task/updateTask", payload)
        } else {
          action = this.$store.dispatch("task/updateFutureTask", payload)
        }
      } else {
        payload.isManuallyCreated = true
        action = this.$store.dispatch("task/addTask", payload)
      }

      this.handleAction(action)
    },
    getDefaultForm() {
      return {
        name: "",
        handle: "",
        description: "",
        processId: null,
        userId:
          this.currentRouteName === "my-task-list" ? this.currentUser?.id : null,
        dueDate: new Date(),
        status: Enums.taskStatusOptions[0].value,
      }
    },
    OnOwnerChange(data) {
      if (this.value?.userId === data) {
        this.permanet = false
      } else {
        this.permanet = true
      }
    },
    gotoUser(item) {
      if (!item) return
      const handle = item.peopleHandle || item.name || item
      const route = this.$router.resolve({
        path: "/people",
        query: {
          q: `peopleHandle=${handle}`,
          qd: handle,
          compact: true,
        },
      })
      window.open(route.href, "_blank")
    },
    gotoProcess(item) {
      if (!item) return
      const handle = item.processHandle || item
      const route = this.$router.resolve({
        path: "/processes",
        query: {
          q: `processHandle=${handle}`,
          qd: handle,
          compact: true,
        },
      })
      window.open(route.href, "_blank")
    },
    gotoRole(item) {
      if (!item) return
      const handle = item.roleHandle || item.name || item
      const route = this.$router.resolve({
        path: "/roles",
        query: {
          q: `roleHandle=${handle}`,
          qd: handle,
          compact: true,
          mine: false,
        },
      })
      window.open(route.href, "_blank")
    },
  },
}
</script>

<style lang="scss">
#mein-expanded-view {
  width: 80% !important;
}
.task-detail-sidebar {
  .b-sidebar-body {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden !important;
    padding: 0 !important;
  }
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>

<style lang="scss" scoped>
@import "../../@core/scss/vue/libs/vue-select";

.template-embed-card {
  background-color: #f1f2f4;
  border: 1px solid #d8d6de !important;
  border-radius: 0.428rem;
}

.ql-editor-preview {
  padding: 0 !important;
  line-height: 1.5;
  p {
    margin-bottom: 0.25rem;
  }
}

.hyperlink-underlined {
  text-decoration: underline;
}
</style>
